import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import EnquiryForm from '../components/EnquiryForm';
import { usePage } from '../contexts/CmsContext';
import { 
  GraduationCap, 
  FileText, 
  Calendar, 
  CheckCircle,
  Download,
  Building2,
  Users,
  Home,
  ArrowRight,
  Phone
} from 'lucide-react';

const ADMISSIONS_HERO = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1920&q=80";

const eligibilityCriteria = [
  'Passed 10+2 examination with Physics, Chemistry, and Biology',
  'Minimum aggregate of 50% marks in PCB (40% for reserved categories)',
  'Qualified NEET-UG examination',
  'Minimum age of 17 years as on 31st December of the admission year',
  'Indian citizen or NRI/OCI as per applicable regulations',
];

const admissionProcess = [
  {
    step: 1,
    title: 'NEET-UG Qualification',
    description: 'Appear and qualify the National Eligibility cum Entrance Test (NEET-UG) conducted by NTA.',
  },
  {
    step: 2,
    title: 'Counselling Registration',
    description: 'Register for state/central counselling as per applicable category and quota.',
  },
  {
    step: 3,
    title: 'Choice Filling & Locking',
    description: 'Fill preferred colleges in order of priority during the counselling process.',
  },
  {
    step: 4,
    title: 'Seat Allotment',
    description: 'Receive seat allotment based on NEET rank, category, and availability.',
  },
  {
    step: 5,
    title: 'Document Verification',
    description: 'Complete document verification at the allotted reporting center.',
  },
  {
    step: 6,
    title: 'Admission & Fee Payment',
    description: 'Report to SSMCH with allotment letter and complete admission formalities.',
  },
];

const ugFees = [
  { batch: 'Batch 2021', amount: '₹ 8,33,000' },
  { batch: 'Batch 2022', amount: '₹ 9,00,000' },
  { batch: 'Batch 2023', amount: '₹ 12,60,000' },
  { batch: 'Batch 2024', amount: '₹ 12,60,000' },
  { batch: 'Batch 2025', amount: '₹ 12,60,000' },
];

const pgFees = [
  { type: 'Clinical', amount: '₹ 9,00,000' },
  { type: 'Non Clinical', amount: '₹ 1,00,000' },
];

const facilities = [
  { icon: Building2, title: 'Modern Campus', description: 'Well-planned campus with academic and residential facilities' },
  { icon: Home, title: 'Hostel Accommodation', description: 'Separate hostels for boys and girls with all amenities' },
  { icon: Users, title: 'Supportive Environment', description: 'Dedicated faculty and supportive staff for student welfare' },
];

const Admissions = () => {
  const { data: admissionsCms } = usePage('admissions');
  const { data: academicsCms } = usePage('academics');
  const overview = admissionsCms?.program_overview || {};
  // Duration, Annual Intake, Recognition tiles are sourced from the Academics
  // page CMS `program_highlights` — Item 1 (index 0), Item 2 (index 1) and
  // Item 4 (index 3) respectively — so editing them in /admin/cms/page/academics
  // updates both the Academics and Admissions pages in one place.
  const highlights = Array.isArray(academicsCms?.program_highlights) ? academicsCms.program_highlights : [];
  const cardsFromAcademics = [highlights[0], highlights[1], highlights[3]].filter(Boolean);
  const overviewCards = cardsFromAcademics.length === 3
    ? cardsFromAcademics
    : (overview.cards || [
        { title: 'Duration', value: '5.5 Years', description: 'Including 1 year internship' },
        { title: 'Annual Intake', value: '200', description: 'Students' },
        { title: 'Recognition', value: 'NMC', description: 'Fully Recognized' },
      ]);
  const cardAccents = ['#4ECDC4', '#D4A84B', '#9ACD32'];
  const cardIcons = [GraduationCap, Users, FileText];
  return (
    <div className="page-transition" data-testid="admissions-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${ADMISSIONS_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="admissions-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#D4A84B] text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Admissions Open 2025-26
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              MBBS Admissions
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8">
              Begin your journey towards becoming a healthcare professional at SSMCH. Applications are now open for the upcoming academic session.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#enquiry-form">
                <Button 
                  size="lg"
                  className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-14 text-lg font-medium shadow-xl hover:shadow-2xl transition-all"
                  data-testid="enquire-now-btn"
                >
                  Enquire Now
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="section-padding bg-white" data-testid="program-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
              {overview.badge || 'MBBS Program'}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              {overview.title || 'Program Overview'}
            </h2>
            <p className="text-gray-600 text-lg">
              {overview.subtitle || 'Our MBBS program is designed to produce competent medical graduates ready for clinical practice.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {overviewCards.map((card, i) => {
              const Icon = cardIcons[i] || GraduationCap;
              const color = cardAccents[i] || '#4ECDC4';
              return (
                <Card key={i} className="border-0 shadow-lg text-center" data-testid={`overview-card-${i}`}>
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${color}1A` }}>
                      <Icon style={{ color }} size={32} />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-2">{card.title}</h3>
                    <p className="text-3xl font-bold" style={{ color }}>{card.value}</p>
                    <p className="text-sm text-gray-600">{card.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eligibility Criteria */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="eligibility-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Requirements
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Eligibility Criteria
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Candidates must meet the following eligibility requirements to apply for MBBS admission at SSMCH.
              </p>
              <ul className="space-y-4">
                {eligibilityCriteria.map((criteria, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-[#4ECDC4] flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-gray-700">{criteria}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-6">Eligibility criteria as per NMC/State regulations.</p>
            </div>
            <div>
              <Card className="border-0 shadow-xl" data-testid="important-dates">
                <CardHeader className="bg-[#1A202C] text-white rounded-t-xl">
                  <CardTitle className="font-heading text-xl flex items-center gap-3">
                    <Calendar size={24} />
                    Important Dates — NEET UG 2026
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {(admissionsCms?.important_dates?.length ? admissionsCms.important_dates : [
                      { label: 'Re-NEET UG exam', date: '21 June 2026', status: 'Confirmed' },
                      { label: 'Results declaration', date: 'July 2026', status: 'Expected' },
                      { label: 'Counselling begins', date: 'August 2026', status: 'Expected' },
                      { label: 'Session commencement', date: 'Sep-Oct 2026', status: 'Expected', highlight: true },
                    ]).map((item, i) => {
                      const isConfirmed = String(item.status).toLowerCase() === 'confirmed';
                      const badgeCls = isConfirmed
                        ? 'bg-green-100 text-green-700'
                        : 'bg-[#D4A84B]/15 text-[#A07F30]';
                      return (
                        <div
                          key={i}
                          className={`flex justify-between items-center p-4 rounded-lg ${item.highlight ? 'bg-[#4ECDC4]/10' : 'bg-gray-50'}`}
                          data-testid={`date-row-${i}`}
                        >
                          <span className="text-gray-700">{item.label}</span>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${item.highlight ? 'text-[#4ECDC4]' : 'text-[#1A202C]'}`}>{item.date}</span>
                            {item.status && (
                              <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-md ${badgeCls}`}>{item.status}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Admission Process */}
      <section className="section-padding bg-white" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
              How to Apply
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Admission Process
            </h2>
            <p className="text-gray-600 text-lg">
              Follow these steps to secure your admission at SSMCH.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {admissionProcess.map((item, index) => (
              <Card 
                key={index}
                className="card-hover border-0 shadow-sm relative overflow-visible"
                data-testid={`process-step-${index}`}
              >
                <CardContent className="p-6 pt-10">
                  <div className="absolute -top-4 left-6 w-10 h-10 bg-[#4ECDC4] rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fee Structure */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="fee-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Annual Tuition Fees
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Fee Structure
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Annual tuition fees, as per regulations prescribed by the state government and applicable authorities.
              </p>

              {/* Undergraduate (MBBS) */}
              <Card className="border-0 shadow-lg mb-6 overflow-hidden">
                <div className="bg-gradient-to-r from-[#1A202C] to-[#2D3748] px-5 py-3">
                  <h3 className="text-white font-heading text-base font-semibold">Undergraduate Course (MBBS)</h3>
                </div>
                <CardContent className="p-0">
                  <table className="w-full" data-testid="fee-table-ug">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Batch</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Annual Tuition Fees</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ugFees.map((item, index) => (
                        <tr key={index} className="border-b border-gray-50 last:border-0 hover:bg-[#4ECDC4]/5 transition-colors">
                          <td className="px-5 py-3.5 text-gray-700 font-medium">{item.batch}</td>
                          <td className="px-5 py-3.5 text-right font-semibold text-[#1A202C]">{item.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Postgraduate */}
              <Card className="border-0 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-[#1A202C] to-[#2D3748] px-5 py-3">
                  <h3 className="text-white font-heading text-base font-semibold">Postgraduate Courses</h3>
                </div>
                <CardContent className="p-0">
                  <table className="w-full" data-testid="fee-table-pg">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stream</th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Annual Tuition Fees</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pgFees.map((item, index) => (
                        <tr key={index} className="border-b border-gray-50 last:border-0 hover:bg-[#4ECDC4]/5 transition-colors">
                          <td className="px-5 py-3.5 text-gray-700 font-medium">{item.type}</td>
                          <td className="px-5 py-3.5 text-right font-semibold text-[#1A202C]">{item.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              <p className="text-xs text-gray-400 mt-4">
                * Annual tuition fees only. Hostel, mess and other charges are billed separately. Fees are subject to revision as per government regulations — please contact the admissions office for current details.
              </p>
            </div>
            <div>
              <span className="inline-block bg-[#9ACD32]/10 text-[#9ACD32] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Facilities
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Hostel & Facilities
              </h2>
              <div className="space-y-6">
                {facilities.map((facility, index) => (
                  <Card key={index} className="border-0 shadow-sm">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-14 h-14 bg-[#4ECDC4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <facility.icon className="text-[#4ECDC4]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1A202C] mb-1">{facility.title}</h3>
                        <p className="text-sm text-gray-600">{facility.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form Section */}
      <section id="enquiry-form" className="section-padding bg-white" data-testid="enquiry-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="inline-block bg-[#4ECDC4]/10 text-[#4ECDC4] px-4 py-2 rounded-full text-sm font-medium mb-4">
                Get in Touch
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-6">
                Admission Enquiry
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Have questions about admissions? Fill out the form and our admissions team will get back to you within 24-48 hours.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center">
                    <Phone className="text-[#4ECDC4]" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A202C]">Admissions Helpline</p>
                    <p className="text-gray-600">+91-9303841084</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D4A84B]/10 rounded-full flex items-center justify-center">
                    <FileText className="text-[#D4A84B]" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A202C]">Email Us</p>
                    <p className="text-gray-600">admissions@ssmch.in</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-[#4ECDC4] to-[#3DBDB4] text-white rounded-t-xl">
                  <CardTitle className="font-heading text-xl">Submit Your Enquiry</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <EnquiryForm sourcePage="admissions" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A202C]" data-testid="admissions-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Need More Information?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Speak with our admissions team for detailed information about the program, facilities, and admission process.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-[#D4A84B] hover:bg-[#C49A3F] text-white rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
              data-testid="cta-contact-btn"
            >
              Contact Admissions
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Admissions;
